import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	TemplateVariableResponse,
	TemplateVariable,
	TemplateVariables,
} from '../../common/types';

export const useTemplateVariables = (projectId: ProjectId) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const templateVariablesRequest = useCallback(
		() =>
			performGetRequest(
				`/rest/servicedesk/notifications/1/outgoing-email-settings/${projectId}/template-panel-data`,
			)
				.then(
					(res: {
						stylingVariables: TemplateVariableResponse[];
						subjectVariables: TemplateVariable[];
					}) => {
						const analyticsEvent = createAnalyticsEvent({
							actionSubject: 'emailTemplatesPanelData',
							action: 'fetched',
						});
						fireOperationalAnalytics(analyticsEvent, 'emailTemplatesPanelData fetched');
						return {
							stylingVariables: res.stylingVariables,
							subjectVariables: res.subjectVariables,
						};
					},
				)
				.catch((error) => {
					fireErrorAnalytics({
						event: createAnalyticsEvent({
							action: 'fetchEmailTemplatesPanelData',
							actionSubject: 'failed',
						}),
						error,
						meta: {
							teamName: 'jsm-nimbus',
							packageName: 'jiraLanguageSettings',
							id: 'useTemplateVariables',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					throw error;
				}),
		[createAnalyticsEvent, projectId],
	);
	return useService<TemplateVariables>(templateVariablesRequest);
};
